import type { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import {
  canInviteAtRole,
  canPromoteMemberTo,
  canRemoveMember as canRemoveMemberByCap,
} from '@indutrust/company-util';
import type { CompanyTeamMember, TeamRole } from '@indutrust/shared-model';
import { APP_SESSION } from '@indutrust/shared-model';
import { resolveHttpError, withRequestStatus } from '@indutrust/shared-util';
import { mapResponse } from '@ngrx/operators';
import {
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  Events,
  injectDispatch,
  on,
  withEventHandlers,
  withReducer,
} from '@ngrx/signals/events';
import { switchMap } from 'rxjs';
import {
  CompanyTeamApiClient,
  type InviteCompanyTeamMemberPayload,
} from '../company-team-api-client.service';
import { companyTeamEvents } from './company-team.events';

type CompanyTeamState = {
  companyId: number | null;
  inviteLoading: boolean;
  members: CompanyTeamMember[];
  removingMemberId: number | null;
  roleChangingMemberId: number | null;
};

const initialState: CompanyTeamState = {
  companyId: null,
  inviteLoading: false,
  members: [],
  removingMemberId: null,
  roleChangingMemberId: null,
};

/** Error token when the last Owner guardrail blocks a mutation. */
export const LAST_OWNER_ERROR = 'last-owner';

/** Error token when R5 same-level cap blocks a mutation. */
export const CAP_VIOLATION_ERROR = 'cap-violation';

function countOwners(members: CompanyTeamMember[]): number {
  return members.filter((member) => member.role === 'owner').length;
}

/**
 * Signal store for Company team management — loads members and handles
 * invite, role change and removal against {@link CompanyTeamApiClient}.
 *
 * Enforces the last-Owner guardrail and R5 same-level cap client-side
 * before mutating calls are dispatched. Server responses remain
 * authoritative: failed HTTP calls surface machine-readable codes
 * (`cap-violation`, `last-owner`, `not_found`, `forbidden`, …) via
 * {@link resolveHttpError} for UI toasts.
 */
export const CompanyTeamStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withRequestStatus(),
  withReducer(
    on(companyTeamEvents.membersLoadRequested, ({ payload }) => ({
      companyId: payload.companyId,
      requestStatus: 'loading' as const,
    })),
    on(companyTeamEvents.membersLoadSucceeded, ({ payload }) => ({
      members: payload.members,
      requestStatus: 'success' as const,
    })),
    on(companyTeamEvents.membersLoadFailed, ({ payload }) => ({
      requestStatus: { error: payload.error },
    })),
    on(companyTeamEvents.inviteRequested, () => ({
      inviteLoading: true,
    })),
    on(companyTeamEvents.inviteSucceeded, ({ payload: member }, state) => ({
      inviteLoading: false,
      members: [...(state as CompanyTeamState).members, member],
    })),
    on(companyTeamEvents.inviteFailed, () => ({
      inviteLoading: false,
    })),
    on(companyTeamEvents.roleChangeRequested, ({ payload }) => ({
      roleChangingMemberId: payload.memberId,
    })),
    on(companyTeamEvents.roleChangeSucceeded, ({ payload: member }, state) => ({
      members: (state as CompanyTeamState).members.map((entry) =>
        entry.id === member.id ? member : entry
      ),
      roleChangingMemberId: null,
    })),
    on(companyTeamEvents.roleChangeFailed, () => ({
      roleChangingMemberId: null,
    })),
    on(companyTeamEvents.removeRequested, ({ payload }) => ({
      removingMemberId: payload.memberId,
    })),
    on(companyTeamEvents.removeSucceeded, ({ payload }, state) => ({
      members: (state as CompanyTeamState).members.filter(
        (entry) => entry.id !== payload.memberId
      ),
      removingMemberId: null,
    })),
    on(companyTeamEvents.removeFailed, () => ({
      removingMemberId: null,
    }))
  ),
  withComputed(({ members, requestStatus }) => ({
    membersLoading: computed(() => requestStatus() === 'loading'),
    ownerCount: computed(() => countOwners(members())),
  })),
  withEventHandlers(
    (store, events = inject(Events), api = inject(CompanyTeamApiClient)) => ({
      inviteMember$: events.on(companyTeamEvents.inviteRequested).pipe(
        switchMap(({ payload }) =>
          api.invite(payload.companyId, payload.payload).pipe(
            mapResponse({
              next: (member) => companyTeamEvents.inviteSucceeded(member),
              error: (error: HttpErrorResponse) =>
                companyTeamEvents.inviteFailed({
                  error: resolveHttpError(error).code,
                }),
            })
          )
        )
      ),
      loadMembers$: events.on(companyTeamEvents.membersLoadRequested).pipe(
        switchMap(({ payload }) =>
          api.getMembers(payload.companyId).pipe(
            mapResponse({
              next: (members) =>
                companyTeamEvents.membersLoadSucceeded({ members }),
              error: (error: HttpErrorResponse) =>
                companyTeamEvents.membersLoadFailed({
                  error: resolveHttpError(error).code,
                }),
            })
          )
        )
      ),
      removeMember$: events.on(companyTeamEvents.removeRequested).pipe(
        switchMap(({ payload }) =>
          api.remove(payload.companyId, payload.memberId).pipe(
            mapResponse({
              next: () =>
                companyTeamEvents.removeSucceeded({
                  memberId: payload.memberId,
                }),
              error: (error: HttpErrorResponse) =>
                companyTeamEvents.removeFailed({
                  error: resolveHttpError(error).code,
                }),
            })
          )
        )
      ),
      updateRole$: events.on(companyTeamEvents.roleChangeRequested).pipe(
        switchMap(({ payload }) =>
          api
            .updateRole(payload.companyId, payload.memberId, {
              role: payload.role,
            })
            .pipe(
              mapResponse({
                next: (member) => companyTeamEvents.roleChangeSucceeded(member),
                error: (error: HttpErrorResponse) =>
                  companyTeamEvents.roleChangeFailed({
                    error: resolveHttpError(error).code,
                  }),
              })
            )
        )
      ),
    })
  ),
  withMethods((store) => {
    const dispatch = injectDispatch(companyTeamEvents);
    const session = inject(APP_SESSION);

    const actorRole = (): TeamRole => session.teamRole();

    const findMember = (memberId: number): CompanyTeamMember | undefined =>
      store.members().find((member) => member.id === memberId);

    const canRemoveMember = (memberId: number): boolean => {
      const member = findMember(memberId);
      if (!member) {
        return false;
      }
      return canRemoveMemberByCap(actorRole(), member, store.members());
    };

    const canDemoteOwner = (memberId: number): boolean => {
      const member = findMember(memberId);
      if (!member) {
        return false;
      }
      if (member.role !== 'owner') {
        return true;
      }
      return canPromoteMemberTo(
        actorRole(),
        member,
        'manager',
        store.members()
      );
    };

    const canChangeMemberRole = (memberId: number, role: TeamRole): boolean => {
      const member = findMember(memberId);
      if (!member) {
        return false;
      }
      return canPromoteMemberTo(actorRole(), member, role, store.members());
    };

    return {
      canChangeMemberRole,
      canDemoteOwner,
      canRemoveMember,
      changeRole(companyId: number, memberId: number, role: TeamRole): void {
        if (!canChangeMemberRole(memberId, role)) {
          const member = findMember(memberId);
          const error =
            member?.role === 'owner' &&
            role !== 'owner' &&
            countOwners(store.members()) === 1
              ? LAST_OWNER_ERROR
              : CAP_VIOLATION_ERROR;
          dispatch.roleChangeFailed({ error });
          return;
        }
        dispatch.roleChangeRequested({ companyId, memberId, role });
      },
      invite(companyId: number, payload: InviteCompanyTeamMemberPayload): void {
        if (!canInviteAtRole(actorRole(), payload.role)) {
          dispatch.inviteFailed({ error: CAP_VIOLATION_ERROR });
          return;
        }
        dispatch.inviteRequested({ companyId, payload });
      },
      loadMembers(companyId: number): void {
        dispatch.membersLoadRequested({ companyId });
      },
      removeMember(companyId: number, memberId: number): void {
        if (!canRemoveMember(memberId)) {
          const member = findMember(memberId);
          const error =
            member?.role === 'owner' && countOwners(store.members()) === 1
              ? LAST_OWNER_ERROR
              : CAP_VIOLATION_ERROR;
          dispatch.removeFailed({ error });
          return;
        }
        dispatch.removeRequested({ companyId, memberId });
      },
    };
  })
);
